import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { useSession } from '@/entities/session'
import { EntityTagsEditor } from '@/entities/tag'
import { type Task, taskDetailQueryOptions } from '@/entities/task'
import { EditTaskDialog, useCompleteTask, useDeleteTask } from '@/features/edit-task'
import { useSearchStore } from '@/features/search'
import { ApiError } from '@/shared/api'
import { Avatar } from '@/shared/ui/avatar'
import { Breadcrumb, type Crumb } from '@/shared/ui/breadcrumb'
import { Button } from '@/shared/ui/button'
import { ConfirmDialog } from '@/shared/ui/confirm-dialog'
import { DateStamp } from '@/shared/ui/date-stamp'
import { Icon } from '@/shared/ui/icon'
import { Spinner } from '@/shared/ui/spinner'
import { StatusBadge } from '@/shared/ui/status-badge'
import { StatusScreen } from '@/shared/ui/status-screen'
import { toast } from '@/shared/ui/toast'
import { CommentThread } from '@/widgets/comment-thread'
import { DocumentSlots } from '@/widgets/document-slots'
import { EntityPageHeader, EntityPageLayout, type EntityPageTab, Meta } from '@/widgets/entity-page'

function PersonMeta({ name }: { name: string }) {
  return (
    <span className="flex items-center gap-2">
      <Avatar name={name} size="sm" />
      <span className="text-sm text-text-primary">{name}</span>
    </span>
  )
}

const TASK_TABS = ['comments', 'docs'] as const
type TaskTab = (typeof TASK_TABS)[number]

const TaskSearchSchema = z.object({
  tab: z.enum(TASK_TABS).optional().catch(undefined),
})

export const Route = createFileRoute('/_app/$company/$project_/tasks/$task')({
  validateSearch: (search) => TaskSearchSchema.parse(search),
  head: () => ({ meta: [{ title: 'Задача · STIT Deal' }] }),
  component: TaskPage,
})

const TAB_LABELS: Record<TaskTab, string> = { comments: 'Комментарии', docs: 'Документы' }

function TaskPage() {
  const { company, project, task: taskId } = Route.useParams()
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { session } = useSession()
  const isEmployee = session?.kind === 'employee'

  const taskQ = useQuery(taskDetailQueryOptions(taskId))
  const complete = useCompleteTask(taskQ.data?.project.id ?? '')
  const del = useDeleteTask(taskQ.data?.project.id ?? '')
  const [editOpen, setEditOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  if (taskQ.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }
  if (taskQ.error) {
    const e = taskQ.error
    if (e instanceof ApiError && e.isForbidden) {
      return <StatusScreen title="Нет доступа" message="Задача недоступна." />
    }
    if (e instanceof ApiError && e.status === 404) {
      return <StatusScreen title="Задача не найдена" message="Проверьте адрес." />
    }
    return <StatusScreen title="Не удалось загрузить задачу" message="Обновите страницу." />
  }

  const t = taskQ.data
  const activeTab: TaskTab = tab ?? 'comments'

  const editTask: Task = {
    id: t.id,
    project_id: t.project.id,
    title: t.title,
    done: t.done,
    created_at: t.created_at,
    // created_by бэк отдаёт всем; fallback — только чтобы удержать тип, в
    // рантайме не достигается.
    created_by: t.created_by ?? { kind: 'employee', id: '' },
    overdue: t.overdue,
    tags: t.tags,
    ...(t.due ? { due: t.due } : {}),
    ...(t.completed_by ? { completed_by: t.completed_by } : {}),
    ...(t.completed_at ? { completed_at: t.completed_at } : {}),
  }

  const tabs: EntityPageTab<TaskTab>[] = TASK_TABS.map((value) => ({
    value,
    label: TAB_LABELS[value],
    content:
      value === 'comments' ? (
        <CommentThread entityKind="task" entityId={t.id} />
      ) : (
        <DocumentSlots
          entityKind="task"
          entityId={t.id}
          docLink={(docId) => ({
            to: '/$company/$project/documents/$doc',
            params: { company, project, doc: docId },
          })}
        />
      ),
  }))

  const statusBadge = t.done ? (
    <StatusBadge status="done" />
  ) : t.overdue ? (
    <StatusBadge status="overdue" />
  ) : (
    <StatusBadge status="open" />
  )

  const header = (
    <EntityPageHeader
      breadcrumb={
        <Breadcrumb
          items={
            [
              isEmployee
                ? { label: t.company.name, to: '/companies/$company', params: { company } }
                : { label: t.company.name },
              { label: t.project.title, to: '/$company/$project', params: { company, project } },
              {
                label: 'Задачи',
                to: '/$company/$project',
                params: { company, project },
                search: { tab: 'tasks' },
              },
              { label: t.title },
            ] satisfies Crumb[]
          }
        />
      }
      title={t.title}
      status={<Meta label="Статус">{statusBadge}</Meta>}
      meta={
        <>
          <Meta label="Создана">
            <DateStamp value={t.created_at} zone className="text-sm" />
          </Meta>
          {t.due && !t.done && (
            <Meta label="Срок">
              <DateStamp value={t.due} deadline className="text-sm" />
            </Meta>
          )}
          {t.created_by?.name && (
            <Meta label="Автор">
              <PersonMeta name={t.created_by.name} />
            </Meta>
          )}
          {t.done && t.completed_at && (
            <Meta label="Выполнена">
              <span className="text-sm text-text-secondary">
                <DateStamp value={t.completed_at} zone className="text-sm" />
                {t.completed_by?.name ? ` · ${t.completed_by.name}` : ''}
              </span>
            </Meta>
          )}
        </>
      }
      {...(isEmployee
        ? {
            tags: (
              <EntityTagsEditor
                entityKind="task"
                entityId={t.id}
                tags={t.tags ?? []}
                onTagClick={(tag) => useSearchStore.getState().openWithTag(tag)}
              />
            ),
          }
        : {})}
      {...(isEmployee
        ? {
            actions: (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={complete.isPending}
                  onClick={() =>
                    complete.mutate(
                      { id: t.id, done: !t.done },
                      { onError: () => toast.error('Не удалось обновить') },
                    )
                  }
                >
                  <Icon name={t.done ? 'chevron-left' : 'check'} size={16} />
                  {t.done ? 'Вернуть' : 'Выполнить'}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
                  <Icon name="pencil" size={16} />
                  Изменить
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-accent"
                  onClick={() => setConfirmDelete(true)}
                >
                  Удалить
                </Button>
              </>
            ),
          }
        : {})}
    />
  )

  return (
    <>
      <EntityPageLayout
        header={header}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(value) => void navigate({ search: { tab: value } })}
        tabsLabel="Разделы задачи"
      />

      {editOpen && (
        <EditTaskDialog
          task={editTask}
          projectId={t.project.id}
          open
          onClose={() => setEditOpen(false)}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Удалить задачу?"
        confirmLabel="Удалить"
        pending={del.isPending}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() =>
          del.mutate(t.id, {
            onSuccess: () => {
              toast.success('Задача удалена')
              void navigate({
                to: '/$company/$project',
                params: { company, project },
                search: { tab: 'tasks' },
              })
            },
            onError: () => toast.error('Не удалось удалить'),
          })
        }
      >
        <p className="text-sm text-text-secondary">Задача и её документы будут удалены.</p>
      </ConfirmDialog>
    </>
  )
}
